/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import * as queryString from "query-string";
import { When } from "react-if";
import { Link } from "react-router-dom";
import { theme, Text, Spacer } from "@nordcloud/gnui";
import {
  useOrgUnitKpiDashboardQuery,
  KpiTimePeriod,
} from "~/generated/graphql";
import { CurrentTarget, KpiDashboardChart, WorstOrgUnits } from "~/components";
import { KpiType } from "~/constants";
import { useCurrency, useQueryState } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty } from "~/tools";
import { OrgUnitKpiWidget } from "../components";
import { mapOrgUnitKpiData } from "../mappers";
import { OrgUnitKpiQueryState } from "../types";
import { useOrgUnitKpiData } from "./hooks";

type Props = {
  orgUnitId: string;
  businessContextId: string;
};

export function OrgUnitKpiTab({ orgUnitId, businessContextId }: Props) {
  const {
    state: {
      kpiType = KpiType.OVERALL_WASTE,
      period = KpiTimePeriod.ThreeMonths,
      childOrgUnit,
    },
    set,
    removeFields,
  } = useQueryState<OrgUnitKpiQueryState>();

  const emptyTargetLink = `${ROUTES.finopsKpi.index}?${queryString.stringify({
    businessContext: businessContextId,
    kpiType,
  })}`;

  const { data, loading, error } = useOrgUnitKpiDashboardQuery({
    variables: { id: orgUnitId },
  });
  const { currency } = useCurrency();

  const {
    overallWaste,
    nonProdHours,
    overallWasteCurrentTarget,
    nonProdHoursCurrentTarget,
  } = React.useMemo(() => mapOrgUnitKpiData(data, currency), [data, currency]);

  const {
    mappedKpiData,
    kpiLoading,
    kpiError,
    mappedWorstChildOrgUnits,
    childOrgUnitName,
  } = useOrgUnitKpiData({
    orgUnitId,
    period,
    kpiType,
    childOrgUnitId: childOrgUnit,
  });

  const handleOrgUnitSelect = ({ id }: { id: string }) => {
    if (childOrgUnit === id) {
      removeFields(["childOrgUnit"]);
    } else {
      set("childOrgUnit")(id);
    }
  };

  const targetData =
    kpiType === KpiType.OVERALL_WASTE
      ? overallWasteCurrentTarget
      : nonProdHoursCurrentTarget;

  const hasTargetData = targetData != null;

  return (
    <>
      <div>
        <OrgUnitKpiWidget
          overallWaste={overallWaste}
          nonProdHours={nonProdHours}
          loading={loading}
          error={error}
          selectedType={kpiType}
        />
      </div>
      <Spacer height={theme.spacing.spacing04} />
      <div>
        <KpiDashboardChart
          kpiType={kpiType}
          data={mappedKpiData}
          error={kpiError}
          loading={kpiLoading}
          period={period ?? KpiTimePeriod.ThreeMonths}
          setPeriod={(input) => set("period")(input)}
          orgUnit={{
            id: childOrgUnit,
            name: childOrgUnitName,
          }}
        />
      </div>
      <Spacer height={theme.spacing.spacing04} />
      <When condition={isNotEmpty(mappedWorstChildOrgUnits)}>
        <WorstOrgUnits
          isNested
          orgUnits={mappedWorstChildOrgUnits}
          selectedOrgUnit={childOrgUnit}
          kpiType={kpiType}
          loading={kpiLoading}
          businessContextId={businessContextId}
          onClick={handleOrgUnitSelect}
        />
      </When>
      <Spacer height={theme.spacing.spacing04} />
      <When condition={hasTargetData && kpiLoading === false}>
        <CurrentTarget
          targetData={targetData}
          loading={kpiLoading}
          kpiType={kpiType}
          editable={false}
        />
      </When>
      <When condition={!hasTargetData && kpiLoading === false}>
        <Text
          weight="medium"
          pb={theme.spacing.spacing01}
          css={{ borderBottom: `1px solid ${theme.color.border.border01}` }}
        >
          Active Target Details
        </Text>
        <Text color={theme.colors.danger} weight="medium" mb={0}>
          The KPI Target is not defined
        </Text>
        <Text color={theme.colors.danger} mb={0}>
          KPI Target can be defined for all Organizational Units in one Business
          Context within the <Link to={emptyTargetLink}>KPI section</Link>.
        </Text>
      </When>
    </>
  );
}
